import * as angular from "angular";
import * as _ from "lodash";
import AddEquipmentName, {openAddEquipment} from "./addEquipment.controller";
import {States} from "../../shared/config/constant.module";
import template from './equipmentList.tpl.html';
import {Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {apsEnums} from "../../shared/dto/aps.enums";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import EquipmentServiceName, {EquipmentService} from "../../services/equipment/equipment.service";
import AuthDataServiceName, {AuthDataService} from "../../services/auth/authData.service";
import UIUtilityServiceName, {UIUtilityService} from "../../services/utility/uiUtility.service";
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";
import LocationsServiceName, {LocationsService, PlantLocation} from "../../services/locations/locations.service";
import EnvironmentName, {Environment} from "../../shared/config/environment";
import UpdateConfigName, {openUpdateConfig} from "./updateConfig.controller";
import EquipmentDiagnosticsName, {openEquipmentDiagnostics} from "./diagnostics.controller";

let requires = [AddEquipmentName, LoaderServiceName, NotifyServiceName, EquipmentServiceName, AuthDataServiceName,
  UIUtilityServiceName, LocationsServiceName, EnvironmentName, NavigationServiceName, UpdateConfigName,
  EquipmentDiagnosticsName];

interface EquipmentListController {
  equipment: apsViewModels.EquipmentDetail[];
  plants: Plant[];
  selectedPlant: Plant;
  plantChanged(): void;
  locationChanged(): void;
  getTypeName(type: apsEnums.EquipmentType): string;
  addEquipment(): void;
  editConfig(instance: apsViewModels.EquipmentDetail): void;
  toggleActive(instance: apsViewModels.EquipmentDetail): void;
  remove(instance: apsViewModels.EquipmentDetail): void;
  openDiagnostics(instance: apsViewModels.EquipmentDetail): void;
  isNotProd: boolean;
}

let ipAddressPattern = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

class EquipmentListCtrl implements EquipmentListController {
  isNotProd: boolean;
  equipment: apsViewModels.EquipmentDetail[];
  filteredEquipment: apsViewModels.EquipmentDetail[];
  plants: Plant[];
  selectedPlant: Plant;
  locations: PlantLocation[];
  selectedLocation: PlantLocation;

  private loaderService: LoaderService;
  private navigationService: NavigationService;
  private equipmentService: EquipmentService;
  private authDataService: AuthDataService;
  private uiUtilityService: UIUtilityService;
  private notifyService: NotifyService;
  private $modal: mm.foundation.modal;
  private locationsService: LocationsService;
  private plantLocations: apsViewModels.LocationNavigation[];

  constructor(
    loaderService: LoaderService,
    equipmentService: EquipmentService,
    navigationService: NavigationService,
    authDataService: AuthDataService,
    uiUtilityService: UIUtilityService,
    notifyService: NotifyService,
    environment: Environment,
    locationsService: LocationsService,
    $modal: mm.foundation.modal
  ) {
    this.loaderService = loaderService;
    this.navigationService = navigationService;
    this.equipmentService = equipmentService;
    this.authDataService = authDataService;
    this.uiUtilityService = uiUtilityService;
    this.notifyService = notifyService;
    this.locationsService = locationsService;
    this.$modal = $modal;

    this.isNotProd = environment.ENV_TYPE !== 'Prod';

    let promise = this.navigationService.getNavigation().then(nav => {
      let plants = nav.plants();
      this.plants = _.filter(plants, (plant: Plant) => {
        return authDataService.isPlantAdmin(plant.id) || authDataService.isAPSIOAdminForPlant(plant.id);
      });

      if (nav.selectedPlant && _.includes(this.plants, nav.selectedPlant)) {
        this.selectedPlant = nav.selectedPlant;
      } else {
        this.selectedPlant = _.first(this.plants);
      }

      this.navigationService.getLocations(false).then(locations => {
        this.plantLocations = locations;
        this.loadLocations();
      });
      return this.loadEquipment();
    });

    this.loaderService.loadingPromise(promise);
  }

  private loadEquipment(): angular.IPromise<any> {
    return this.equipmentService
      .get(this.selectedPlant.id)
      .then(equipment => {
        this.equipment = equipment;
        this.filterEquipment();
      });
  }

  private filterEquipment(): void {
    if (this.selectedLocation) {
      this.filteredEquipment = _.filter(this.equipment, {plantLocationId: this.selectedLocation.location.id});
    } else {
      this.filteredEquipment = this.equipment;
    }
  }

  private loadLocations(): void {
    this.locations = this.locationsService
      .getPlantLocations(this.selectedPlant, this.plantLocations);
    this.selectedLocation = null;
  }

  plantChanged(): void {
    this.loadLocations();
    this.loaderService.loadingPromise(this.loadEquipment());
  }

  locationChanged(): void {
    this.filterEquipment();
  }

  getTypeName(type: apsEnums.EquipmentType): string {
    return _.startCase(apsEnums.EquipmentType[type]);
  }

  addEquipment(): void {
    let result = openAddEquipment({
      plant: this.selectedPlant,
      locations: this.locations,
      isNotProd: this.isNotProd,
      allEquipment: this.equipment,
      ipAddressPattern: ipAddressPattern
    }, this.$modal);

    result.then(instance => {
      if (!instance) {
        return;
      }

      this.equipment.push(instance);
      this.filterEquipment();
    });
  }

  editConfig(instance: apsViewModels.EquipmentDetail): void {
    openUpdateConfig({
      instance: instance,
      plantId: this.selectedPlant.id,
      locations: this.locations,
      allEquipment: this.equipment,
      isNotProd: this.isNotProd,
      ipAddressPattern: ipAddressPattern
    }, this.$modal);
  }

  toggleActive(instance: apsViewModels.EquipmentDetail): void {
    if (instance.active) {
      this.uiUtilityService.confirm('Are you sure you want to disable this equipment?').then(confirmed => {
        if (!confirmed) {
          return;
        }

        this.loaderService.loadingPromise(this.equipmentService.disable({
          id: instance.id,
          plantId: this.selectedPlant.id
        }).then(() => {
          instance.active = false;
        }, this.notifyService.handleApiErrors));
      });
    } else {
      this.loaderService.loadingPromise(this.equipmentService.activate({
        id: instance.id,
        plantId: this.selectedPlant.id
      }).then(() => {
        instance.active = true;
      }, this.notifyService.handleApiErrors));
    }
  }

  remove(instance: apsViewModels.EquipmentDetail): void {
    this.uiUtilityService.confirm('Are you sure you want to delete this equipment?').then(confirmed => {
      if (!confirmed) {
        return;
      }

      this.loaderService.loadingPromise(this.equipmentService.remove({
        id: instance.id,
        plantId: this.selectedPlant.id
      }).then(() => {
        _.remove(this.equipment, instance);
        this.filterEquipment();
      }, this.notifyService.handleApiErrors));
    });
  }

  openDiagnostics(instance: apsViewModels.EquipmentDetail): void {
    openEquipmentDiagnostics({
      plantId: this.selectedPlant.id,
      equipment: instance,
      timeZoneId: this.selectedPlant.timeZoneId
    }, this.$modal);
  }
}

export default angular.module('aps.controller.equipmentList', requires)
  .config(($stateProvider: angular.ui.IStateProvider) => {
    $stateProvider.state({
      name: States.EQUIPMENT,
      url: '/admin/equipment',
      controller: 'EquipmentListCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Equipment',
        authorizeAPSIOAdmin: true
      }
    });
  })
  .controller('EquipmentListCtrl', EquipmentListCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/equipment/equipmentList.controller.ts